import type { CursorPaginationQuery } from "@/utils/utilityTypes"

type UsePaginationOptions = {
  count: MaybeRef<number>
  limit: number
}

/**
 * For use with endpoints utilising the default `CustomLimitOffsetPagination`
 * @param options Pagination options.
 * @param options.count The total count of entities being paginated. Likely returned from API.
 * @param options.limit How many entities to display per page. Likely set in API request.
 * @returns
 */
export const usePagination = ({
  count,
  limit,
}: UsePaginationOptions) => {
  const paginationQuery = ref<Required<PaginationQuery>>({
    limit,
    offset: 0,
  })

  const resetPagination = () => {
    paginationQuery.value = {
      limit,
      offset: 0,
    }
  }

  const page = computed({
    get: () =>
      Math.floor(
        paginationQuery.value.offset /
          paginationQuery.value.limit
      ) + 1,
    set: (value) => {
      paginationQuery.value.offset =
        paginationQuery.value.limit * (value - 1)
    },
  })

  const pageCount = computed(() =>
    Math.max(
      1,
      Math.ceil(unref(count) / paginationQuery.value.limit)
    )
  )

  return {
    page,
    pageCount: readonly(pageCount),
    paginationQuery: readonly(paginationQuery),
    resetPagination,
  }
}

type UseHasNextPaginationOptions = {
  hasNext: MaybeRef<boolean>
  limit: number
}

/**
 * For use with endpoints utilising `HasNextLimitOffsetPagination`
 * @param options Pagination options.
 * @param options.hasNext Whether proceeding to next page should be permitted. Likely returned from API.
 * @param options.limit How many entities to display per page. Likely set in API request.
 * @returns
 */
export const useHasNextPagination = ({
  hasNext,
  limit,
}: UseHasNextPaginationOptions) => {
  const paginationQuery = ref<Required<PaginationQuery>>({
    limit,
    offset: 0,
  })

  const resetPagination = () => {
    paginationQuery.value = {
      limit,
      offset: 0,
    }
  }

  const page = computed({
    get: () =>
      Math.floor(
        paginationQuery.value.offset /
          paginationQuery.value.limit
      ) + 1,
    set: (value) => {
      paginationQuery.value.offset =
        paginationQuery.value.limit * (value - 1)
    },
  })

  const enableNext = computed(() => unref(hasNext))

  const enablePrevious = computed(() => page.value > 1)

  return {
    paginationQuery: readonly(paginationQuery),
    enableNext,
    enablePrevious,
    page,
    resetPagination,
  }
}

type UseCursorPaginationOptions<T extends object> = {
  initialCursor: T
  nextCursor: ComputedRef<T>
  enableNext: MaybeRef<boolean>
  limit: number
}

/**
 * For use with endpoints utilising some form of custom cursor pagination
 * @param options Pagination options.
 * @param options.initialCursor The starting value for the current cursor.
 * @param options.nextCursor The prospective value for the current cursor when page is incremented
 * @param options.enableNext Whether to enable futher incrementation of the page. Will usually be derived from `nextCursor`
 * @param limit How many results to show per page.
 * @returns
 */
export const useCursorPagination = <
  TCursor extends object,
>({
  initialCursor,
  nextCursor,
  enableNext,
  limit,
}: UseCursorPaginationOptions<TCursor>) => {
  const currentCursor = ref<TCursor>(initialCursor)
  const cursorStack = ref<Array<TCursor>>([])

  const paginationQuery = computed<
    CursorPaginationQuery<TCursor>
  >(() => ({
    limit,
    ...currentCursor.value,
  }))

  const resetPagination = () => {
    cursorStack.value = []
    currentCursor.value = initialCursor
  }

  const enablePrevious = computed(
    () => cursorStack.value.length > 0
  )

  const page = computed<number>({
    get: () => cursorStack.value.length + 1,
    set: (newPageNumber) => {
      if (newPageNumber > cursorStack.value.length + 2) {
        throw new Error(
          "Page number can only be incremented by 1 when using cursor pagination"
        )
      }

      if (newPageNumber === cursorStack.value.length + 2) {
        cursorStack.value = [
          ...cursorStack.value,
          currentCursor.value,
        ]
        currentCursor.value = unref(nextCursor)
        return
      }

      currentCursor.value =
        cursorStack.value[newPageNumber - 1]
      cursorStack.value = cursorStack.value.slice(
        0,
        newPageNumber - 1
      )
    },
  })

  return {
    paginationQuery: readonly(paginationQuery),
    enableNext,
    enablePrevious,
    page,
    resetPagination,
  }
}
